// ═══════════════════════════════════════════════════════════════
// MODAL: Crear/Editar Otras Deducciones
// Con selector de quincena (Q1, Q2, AMBAS)
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';
import { useCompany } from '../../../contexts/CompanyContext';
import { createOtherDeduction, updateOtherDeduction } from '../../../api/otherDeductions';

export default function OtherDeductionModal({ deduction, employees, deductionTypes, onClose, onSuccess }) {
  const { currentCompany } = useCompany();
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    employeeId: '',
    tipo: '',
    concepto: '',
    monto: '',
    categoria: '',
    recurrente: false,
    numeroCuotas: '',
    cuotaActual: '',
    quincenaDescuento: 'Q1' // ⭐ NUEVO
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (deduction) {
      setFormData({
        employeeId: deduction.employee_id || '',
        tipo: deduction.tipo || '',
        concepto: deduction.concepto || '',
        monto: deduction.monto || '',
        categoria: deduction.categoria || '',
        recurrente: deduction.recurrente || false,
        numeroCuotas: deduction.numero_cuotas || '',
        cuotaActual: deduction.cuota_actual || '',
        quincenaDescuento: deduction.quincena_descuento || 'Q1' // ⭐ NUEVO
      });
    }
  }, [deduction]);

  const validate = () => {
    const newErrors = {};

    if (!formData.employeeId) newErrors.employeeId = 'Selecciona un empleado';
    if (!formData.tipo) newErrors.tipo = 'Selecciona un tipo';
    if (!formData.concepto.trim()) newErrors.concepto = 'Ingresa un concepto';
    if (!formData.monto || parseFloat(formData.monto) <= 0) {
      newErrors.monto = 'El monto debe ser mayor a 0';
    }

    // Si tiene cuotas, validar
    if (formData.numeroCuotas && parseInt(formData.numeroCuotas) <= 0) {
      newErrors.numeroCuotas = 'El número de cuotas debe ser mayor a 0';
    }

    if (formData.cuotaActual && parseInt(formData.cuotaActual) < 0) {
      newErrors.cuotaActual = 'La cuota actual no puede ser negativa';
    }

    if (formData.numeroCuotas && formData.cuotaActual) {
      if (parseInt(formData.cuotaActual) > parseInt(formData.numeroCuotas)) {
        newErrors.cuotaActual = 'La cuota actual no puede ser mayor al total';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    try {
      setSaving(true);

      const dataToSave = {
        companyId: currentCompany.id,
        employeeId: formData.employeeId,
        tipo: formData.tipo,
        concepto: formData.concepto.trim(),
        monto: parseFloat(formData.monto),
        categoria: formData.categoria.trim() || null,
        recurrente: formData.recurrente,
        numeroCuotas: formData.numeroCuotas ? parseInt(formData.numeroCuotas) : null,
        cuotaActual: formData.cuotaActual ? parseInt(formData.cuotaActual) : null,
        quincenaDescuento: formData.quincenaDescuento // ⭐ NUEVO
      };

      if (deduction) {
        await updateOtherDeduction(deduction.id, dataToSave);
      } else {
        await createOtherDeduction(dataToSave);
      }

      alert(`✅ Deducción ${deduction ? 'actualizada' : 'creada'} exitosamente`);
      onSuccess();
    } catch (error) {
      console.error('Error saving deduction:', error);
      alert('❌ Error al guardar deducción: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const selectedEmployee = employees.find(e => e.id === formData.employeeId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-red-600 to-orange-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-2xl font-bold">
            {deduction ? 'Editar Deducción' : 'Nueva Deducción'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Employee Selection */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Empleado *
            </label>
            <select
              value={formData.employeeId}
              onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors ${
                errors.employeeId ? 'border-red-500' : 'border-gray-200 focus:border-red-500'
              }`}
              disabled={!!deduction}
            >
              <option value="">Seleccionar empleado...</option>
              {employees.filter(e => e.estado === 'activo').map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.nombre} {emp.apellido} - {emp.employee_id}
                </option>
              ))}
            </select>
            {errors.employeeId && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.employeeId}
              </p>
            )}
          </div>

          {/* Selected Employee Info */}
          {selectedEmployee && (
            <div className="p-4 bg-blue-50 rounded-xl">
              <p className="text-sm text-blue-900">
                <span className="font-bold">Salario:</span> RD$ {parseFloat(selectedEmployee.salario || 0).toLocaleString('es-DO')}
              </p>
              <p className="text-sm text-blue-900 mt-1">
                <span className="font-bold">Departamento:</span> {selectedEmployee.departamento || 'N/A'}
              </p>
            </div>
          )}

          {/* Type */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Tipo de Deducción *
            </label>
            <select
              value={formData.tipo}
              onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors ${
                errors.tipo ? 'border-red-500' : 'border-gray-200 focus:border-red-500'
              }`}
            >
              <option value="">Seleccionar tipo...</option>
              {deductionTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {errors.tipo && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.tipo}
              </p>
            )}
          </div>

          {/* Concepto */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Concepto *
            </label>
            <textarea
              value={formData.concepto}
              onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
              rows={3}
              placeholder="Ej: Seguro médico familiar..."
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors resize-none ${
                errors.concepto ? 'border-red-500' : 'border-gray-200 focus:border-red-500'
              }`}
            />
            {errors.concepto && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.concepto}
              </p>
            )}
          </div>

          {/* Monto and Categoria */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Monto *
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-bold">
                  RD$
                </span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.monto}
                  onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                  placeholder="0.00"
                  className={`w-full pl-14 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-colors ${
                    errors.monto ? 'border-red-500' : 'border-gray-200 focus:border-red-500'
                  }`}
                />
              </div>
              {errors.monto && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.monto}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Categoría
              </label>
              <input
                type="text"
                value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                placeholder="Ej: Seguros"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:outline-none transition-colors"
              />
            </div>
          </div>

          {/* ⭐ NUEVO: Selector de Quincena */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Descontar en Quincena *
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, quincenaDescuento: 'Q1' })}
                className={`px-4 py-3 rounded-xl font-semibold transition-all ${
                  formData.quincenaDescuento === 'Q1'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Primera Quincena (Q1)
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, quincenaDescuento: 'Q2' })}
                className={`px-4 py-3 rounded-xl font-semibold transition-all ${
                  formData.quincenaDescuento === 'Q2'
                    ? 'bg-green-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Segunda Quincena (Q2)
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, quincenaDescuento: 'AMBAS' })}
                className={`px-4 py-3 rounded-xl font-semibold transition-all ${
                  formData.quincenaDescuento === 'AMBAS'
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Ambas (Q1 + Q2)
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {formData.quincenaDescuento === 'AMBAS' 
                ? 'Se descontará la mitad del monto en cada quincena'
                : `Se descontará el monto completo en la ${formData.quincenaDescuento === 'Q1' ? 'primera' : 'segunda'} quincena`
              }
            </p>
          </div>

          {/* Recurrente Checkbox */}
          <div className="flex items-start gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl">
            <input
              type="checkbox"
              id="recurrente"
              checked={formData.recurrente}
              onChange={(e) => setFormData({ ...formData, recurrente: e.target.checked })}
              className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <div>
              <label htmlFor="recurrente" className="text-sm font-semibold text-gray-700 cursor-pointer">
                Deducción permanente
              </label>
              <p className="text-xs text-gray-400 mt-0.5">
                Marcada: se mantiene activa nómina tras nómina. Sin marcar: se desactiva una vez aplicada (usa cuotas para pagos a plazos).
              </p>
            </div>
          </div>

          {/* Cuotas (opcional) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Número de Cuotas
              </label>
              <input
                type="number"
                value={formData.numeroCuotas}
                onChange={(e) => setFormData({ ...formData, numeroCuotas: e.target.value })}
                placeholder="Ej: 12"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors ${
                  errors.numeroCuotas ? 'border-red-500' : 'border-gray-200 focus:border-red-500'
                }`}
              />
              {errors.numeroCuotas && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.numeroCuotas}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">Opcional: Para pagos a plazos</p>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Cuota Actual
              </label>
              <input
                type="number"
                value={formData.cuotaActual}
                onChange={(e) => setFormData({ ...formData, cuotaActual: e.target.value })}
                placeholder="Ej: 0"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors ${
                  errors.cuotaActual ? 'border-red-500' : 'border-gray-200 focus:border-red-500'
                }`}
              />
              {errors.cuotaActual && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.cuotaActual}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">Empieza en 0</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {deduction ? 'Actualizar' : 'Crear'} Deducción
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
